import { Link } from 'react-router-dom'
import type { SiteEvent } from '../api/public'
import { useEffect, useMemo, useState } from 'react'
import {
  formatCrtTodayDots,
  relativeTransmissionLabel,
  splitTitleForCrt,
} from '../lib/event-format'
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion'
import { useHomeCrtChannel } from '../context/HomeCrtChannelContext'
import { SlideTitle } from './SlideTitle'

const ROTATE_MS = 6000
const MAX_SLIDES = 8
const BG_ROUND = ['bg-1', 'bg-2', 'bg-3', 'bg-4'] as const

function eyebrow(ev: SiteEvent | null, index: number, total: number): string {
  if (!ev) return '> SCHEDULE'
  if (total <= 1) return '> FEATURED'
  return `› ${String(index + 1).padStart(2, '0')}/${String(total).padStart(2, '0')} · EVENT`
}

export function HomeHero({ events, loading }: { events?: SiteEvent[]; loading?: boolean }) {
  const list = useMemo(() => (events ?? []).slice(0, MAX_SLIDES), [events])
  const reduced = usePrefersReducedMotion()
  const [i, setI] = useState(0)
  const { setHomeCrtChannel } = useHomeCrtChannel()

  useEffect(() => {
    setI(0)
  }, [list.length])

  useEffect(() => {
    if (reduced || list.length <= 1) return
    const id = window.setInterval(() => {
      setI((k) => (k + 1) % list.length)
    }, ROTATE_MS)
    return () => window.clearInterval(id)
  }, [list.length, reduced])

  const activeIdx = list.length === 0 ? 0 : Math.min(i, list.length - 1)
  const chNum = String(activeIdx + 1).padStart(2, '0')
  const airDate = formatCrtTodayDots()

  useEffect(() => {
    setHomeCrtChannel({ ch: chNum, air: airDate })
    return () => setHomeCrtChannel(null)
  }, [chNum, airDate, setHomeCrtChannel])

  const total = Math.max(list.length, 1)

  return (
    <section className="hero" aria-label="Kommende Highlights">
      <div className="slides" id="slides">
        {loading ? (
          <div className="slide active">
            <div className={`slide-bg ${BG_ROUND[0]}`} />
            <div className="slide-top">
              <span>
                <span className="num">› …</span> &nbsp;LOADING
              </span>
              <span className="num">WAIT</span>
            </div>
            <div className="slide-mid">
              <SlideTitle lines={['FETCH', 'SCHEDULE']} />
            </div>
          </div>
        ) : list.length === 0 ? (
          <div className="slide active">
            <div className={`slide-bg ${BG_ROUND[0]}`} />
            <div className="slide-top">
              <span>
                <span className="num">› 00/00</span> &nbsp;BNKR
              </span>
              <span className="num">IDLE</span>
            </div>
            <div className="slide-mid">
              <SlideTitle lines={['KEINE', 'TERMINE']} />
              <div className="slide-meta">
                <span className="pill">—</span>
                <span>
                  <strong>BNKR.CH</strong> · Events bald wieder hier
                </span>
              </div>
              <div className="slide-cta">
                <Link className="btn-solid-crt" to="/events">
                  Alle Events →
                </Link>
              </div>
            </div>
          </div>
        ) : (
          list.map((ev, idx) => {
            const lines = splitTitleForCrt(ev.title, 3)
            return (
              <div key={ev.id} className={`slide ${idx === activeIdx ? 'active' : ''}`} data-i={idx}>
                <div className={`slide-bg ${BG_ROUND[idx % BG_ROUND.length]}`} />
                <div className="slide-top">
                  <span>
                    <span className="num">{eyebrow(ev, idx, list.length)}</span>
                  </span>
                  <span className="num">{ev.displayStatus === 'upcoming' ? 'UPCOMING' : ''}</span>
                </div>
                <div className="slide-mid">
                  <SlideTitle lines={lines} />
                  <div className="slide-meta">
                    <span className="pill">
                      {new Date(ev.startsAt).toLocaleDateString('de-CH', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      })}
                    </span>
                    {(ev.venueName || ev.locationLabel) ? (
                      <span>{(ev.venueName || ev.locationLabel || '').toUpperCase()}</span>
                    ) : null}
                    <span className="slide-meta-countdown">{relativeTransmissionLabel(ev.startsAt)}</span>
                  </div>
                  <div className="slide-cta">
                    <Link className="btn-solid-crt" to={`/events/${ev.slug}`}>
                      Details →
                    </Link>
                    {ev.ticketUrl ? (
                      <Link className="btn-ghost-crt" to={`/events/${ev.slug}/tickets`}>
                        Tickets
                      </Link>
                    ) : (
                      <Link className="btn-ghost-crt" to={`/events/${ev.slug}`}>
                        Mehr
                      </Link>
                    )}
                  </div>
                </div>
                <div className="slide-bottom">
                  <span>▮ LOADING · BUNKER EVENTS · CH</span>
                  <span className="hide-sm">SCROLL ↓</span>
                </div>
              </div>
            )
          })
        )}
      </div>

      {!loading && list.length > 1 ? (
        <div className="hero-pager-bar">
          <div className="pager" role="tablist" aria-label="Slides">
            {list.map((ev, idx) => (
              <button
                key={ev.id}
                type="button"
                className={`dot ${idx === activeIdx ? 'on' : ''}`}
                aria-selected={idx === activeIdx}
                aria-label={`Slide ${idx + 1}`}
                data-i={idx}
                onClick={() => setI(idx)}
              />
            ))}
          </div>
        </div>
      ) : null}

      {!loading && list.length > 1 ? (
        <div className="hero-arrows">
          <button
            type="button"
            className="prev"
            aria-label="Vorheriges"
            onClick={() => setI((k) => (k - 1 + list.length) % list.length)}
          >
            ‹
          </button>
          <button
            type="button"
            className="next"
            aria-label="Nächstes"
            onClick={() => setI((k) => (k + 1) % list.length)}
          >
            ›
          </button>
        </div>
      ) : null}

      <div className="crt-fx" aria-hidden />
      <div className="crt-sweep" aria-hidden />
    </section>
  )
}
